// Gotta tree shake this stuff
import {
	toSafeInteger as _sint, get as _get,
	max as _arrayMax,
} from 'lodash';

import {select as d3select, scaleLinear as d3scale, area as d3area, line as d3line} from 'd3';

const numeral = require("numeral");
const SafeString = require('nunjucks').runtime.SafeString;

const TS = require('@zerolight/zlthunder-time-utils');
const {IOProcess} = require('@zerolight/zlthunder-ioprocess');

const APIController = require('../../shared/api-controller');
const {mergeCustomiser} = require('../../shared/api-client');

const ProcessingIconTemplate = require('../../templates/processing-icon.html');
const PROCESSING = new SafeString(ProcessingIconTemplate.render());

const SUMMARY_VALUES_FIELDS = new Set(['count', 'total', 'min', 'max', 'ccu']);
const SUMMARY_STATE_FIELDS = new Set(['name', 'stale', 'updated']);

function _sAdd(a, b) {
	return _sint(a) + _sint(b);
}

function _sMax() {
	let args = Array.from(arguments).filter(x => !isNaN(x));
	return Math.max(... args);
}

function _sMin() {
	let args = Array.from(arguments).filter(x => !isNaN(x));
	return Math.min(... args);
}

class Summary {
	constructor(data) {
		this._stale = false;
		this._name = '';
		this._values = {};
		if (data) {
			this.addSummary(data);
		}
	}

	get stale() {
		return Boolean(this._stale);
	}

	get name() {
		return this._name;
	}

	setStale() {
		this._stale = true;
		this._values = {};
	}

	get(key) {
		if (!this.stale) {
			return this._values[key];
		}
		return PROCESSING;
	}

	_addValues(data) {
		let {count, total, min, max, ccu} = data;
		if (this.stale) {
			return;
		}
		let vals = this._values;
		vals.count = _sint(vals.count) + _sint(count);
		vals.total = _sint(vals.total) + _sint(total);
		vals.min = _sMin(vals.min, min);
		vals.max = _sMax(vals.max, max);
		vals.ccu = _sMax(vals.ccu, ccu);
	}

	addSummary(data) {
		is_stale: {
			if (this.stale) {
				break is_stale;
			}
			if (data.stale) {
				this.setStale();
				break is_stale;
			}

			this._name = this._name || data.name;
			if (data instanceof Summary) {
				this._addValues(data._values);
			} else {
				this._addValues(data);
			}
		}
		return this;
	}

	get avg() {
		if (this.stale) {
			return PROCESSING;
		}
		let {total, count} = this._values;
		return new Avg(total, count);
	}
}

function totalGetter(v) {
	return new Summary(v).get('total') || undefined;
}

function countGetter(v) {
	return new Summary(v).get('count') || undefined;
}

class Avg {
	constructor(total, count) {
		this.total = total;
		this.count = count;
	}

	valueOf() {
		if (this.count) {
			return this.total / this.count;
		}
		return 0;
	}

	toString() {
		return this.valueOf();
	}
}

function avgGetter(v) {
	let s = new Summary(v);
	if (s.stale) {
		return PROCESSING;
	}
	let c = s.get('count');
	if (!c) {
		return undefined;
	}
	return new Avg(s.get('total'), c);
}

function ccuGetter(v) {
	let ccu = new Summary(v).get('ccu');
	if (ccu === -Infinity || isNaN(ccu)) {
		ccu = undefined;
	}
	return ccu;
}

function formatter(v) {
	if (v === PROCESSING) {
		return PROCESSING;
	}
	if (v === undefined || v === null) {
		return '';
	}
	if (_.isNumber(v)) {
		return numeral(v).format('0,0');
	}
	return String(v);
}

function minutesFormatter(v) {
	if (v && _.isNumber(v)) {
		return numeral(Math.ceil(v / TS.MINUTE)).format('0,0');
	}
	return formatter(v);
}

function sessionLengthFormatter(v) {
	if (v instanceof Avg) {
		v = v.valueOf();
	} else if (isNaN(v)) {
		return formatter(v);
	}

	v = v / TS.SECOND;
	let m = Math.floor(v / 60);
	let s = Math.round(v % 60);
	if (s === 60) {
		m += 1;
		s = 0;
	}
	return `${m.toString()}:${s.toString().padStart(2, '0')}`;
}

function reduceTotal(a, b) {
	if (a === PROCESSING || b === PROCESSING) {
		return PROCESSING;
	}
	return _sint(a) + _sint(b);
}

function mergeSummarySets() {
	let re = {};
	for (let c of Array.from(arguments)) {
		if (!c) {
			continue;
		}
		for (let k of Object.keys(c)) {
			let s = re[k];
			if (!s) {
				s = new Summary();
			}
			s.addSummary(c[k]);
			re[k] = s;
		}
	}
	return re;
}

function dashboardMergeCustomiser(o, s, key) {
	/*
	// Intercept on the top level
	valid_data: {
		if (key !== 'data') {
			break valid_data;
		}
		let {sparks, tabs} = s;
		if (!(sparks && tabs)) {
			console.warn('Got malformed dashboard data from server', s);
			return o || {};
		}
		let re = {
			sparks: mergeSummarySets(o && o.sparks, s.sparks),
			tabs: mergeSummarySets(o && o.tabs, s.tabs)
		};
		console.debug('Dashboard data merged', re, o, s);
		return re;
	}
	// return mergeCustomiser(... arguments);
	*/
}

const TABS = ['h1', 'h24', 'day7', 'day30', 'year', 'life'];
function getRow(data, title, getter, formatter) {
	let row = {title};
	for (let t of TABS) {
		let s = _get(data, `tabs.${t}`);
		if (!s) {
			return undefined;
		}
		if (getter) {
			s = getter(s);
		}
		if (formatter) {
			s = formatter(s);
		}
		row[t] = s;
	}

	let sparks = [];
	let to = TS.hour(new Date()).valueOf();
	let from = to - TS.HOUR * 4;
	for (let ts of TS.domain(from, to)) {
		ts = ts.valueOf();
		let s = _get(data.sparks, ts.toString());
		if (!s) {
			sparks.push(0);
			continue;
		}
		s = new Summary(s);
		if (s.stale) {
			sparks.push(0);
			continue;
		}
		if (getter) {
			sparks.push(getter(s));
		} else {
			sparks.push(s);
		}
	}
	sparks.reverse();
	row.sparks = sparks;
	return row;
}

function sparkLine(cell) {
	let s = d3select(this); // jshint ignore:line
	s.text(null);

	let spark = s.append('div').attr('class', 'cm-spark').append('svg');
	let node = spark.node();
	if (!node) {
		return;
	}
	let rect = node.getBoundingClientRect();
	let w = rect.width, h = rect.height;

	let data = cell.data.sparks;
	if (data) {
		let xScale = d3scale().domain([0, data.length - 1]).rangeRound([1, w - 1]);
		let yScale = d3scale().domain([0, _arrayMax(data)]).rangeRound([1, h - 1]);
		let area = d3area()
			.x(function (d, i) { return xScale(i); })
			.y0(h + 5)
			.y1(function (d) { return (h - yScale(d)) || h; });
		let line = d3line()
			.x(function (d, i) { return xScale(i); })
			.y(function (d) { return (h - yScale(d)) || h; });

		spark.append('g').attr('class', 'area').append('path').attr('d', area(data));
		spark.append('g').attr('class', 'line').append('path').attr('d', line(data));
	}
}

function isStale(data) {
	let {tabs, sparks} = data;
	for (let s of Object.values(tabs)) {
		if (s.stale) {
			return true;
		}
	}
	for (let s of Object.values(sparks)) {
		if (s.stale) {
			return true;
		}
	}
	return false;
}

class RefreshJob extends IOProcess {
	constructor(controller, apiCallOptions) {
		super({logger: console, loopTimeout: 1000, stopOnErrors: true});
		this._controller = controller;
		this._apiCallOptions = Object.assign({}, apiCallOptions);
	}

	attachToController() {
		let controller = this._controller;
		if (controller._refreshJob) {
			this.detachJobFromController(controller);
		}
		controller._refreshJob = this;
		let {container} = controller;
		if (container) {
			container.classList.add('cm-refreshing');
		}
	}

	detachJobFromController() {
		let controller = this._controller;
		let job = controller._refreshJob;
		if (job) {
			controller._refreshJob = null;
		}
		let c = controller.container;
		if (c) {
			c.classList.remove('cm-refreshing');
		}
	}

	run() {
		this.attachToController();
		super.run();
	}

	stop() {
		this.detachJobFromController(this._controller);
		super.stop();
	}

	loop() {
		let self = this;
		let controller = this._controller;
		let callOptions = this._apiCallOptions;
		controller.apiCall(callOptions)
			.then(function (response) {
				return controller.update({data: response})
					.then(function ({stale, data}) {
						if (stale || (data && data.stale)) {
							self.next();
						} else {
							self.stop();
						}
					});
			})
			.catch(function (e) {
				self.next(e);
			});
	}
}

class SelfReloadingController extends APIController {
	load(options) {
		let self = this;
		// console.debug('SelfReloadingController.load', self.constructor.name, 'Starting loading');
		return super.load(options)
			.then(function (r) {
				let {stale, data} = r;
				// console.debug('SelfReloadingController.load', self.constructor.name, stale);
				if (stale || (data && data.stale)) {
					new RefreshJob(self, options).run();
				}
				return r;
			});
	}

	reload(options) {
		let self = this;
		// console.debug('SelfReloadingController.load', self.constructor.name, 'Starting reloading');
		return super.reload(options)
			.then(function (r) {
				let {stale, data} = r;
				// console.debug('SelfReloadingController.reload', self.constructor.name, stale);
				if (stale || (data && data.stale)) {
					new RefreshJob(self, options).run();
				}
				return r;
			});
	}

	destroy() {
		super.destroy();
		if (self._refreshJob) {
			self._refreshJob.stop();
		}
	}
}


export {
	totalGetter, countGetter, avgGetter, ccuGetter,
	formatter, minutesFormatter, sessionLengthFormatter, reduceTotal,
	getRow, sparkLine, isStale, RefreshJob, SelfReloadingController,
	PROCESSING, dashboardMergeCustomiser
};

